import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { observer } from "mobx-react-lite"

import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { makeBreadcrumbs } from "@/domains/lock-page/breadcrumbs"
import { WithdrawCard } from "@/domains/lock-page/components/card/withdraw-card/components/withdraw-card"
import { LockPageLayout } from "@/domains/lock-page/components/lock-page-layout"
import { LockPageTabs } from "@/domains/lock-page/components/lock-page-tabs"
import { MANAGE_LOCK_TABS } from "@/domains/lock-page/constants"
import { ComplexTab } from "@/domains/navigation/components/complex-breadcrumb-dropdown"
import { useGovernanceToken } from "@/hooks/use-governance-token"
import { useNavigateOnLockCleared } from "@/hooks/use-navigate-on-lock-cleared"
import { useSyncLockIdFromRouter } from "@/hooks/use-sync-lock-id-from-router"

export default observer(() => {
  const navigate = useNavigate()

  const governanceToken = useGovernanceToken()
  const scaleApp = useScaleApplication()
  const breadcrumbs = makeBreadcrumbs()

  useSyncLockIdFromRouter()
  useNavigateOnLockCleared()

  const orchestrator = scaleApp.Pages.Lock.WithdrawOrchestrator

  // Handle successful withdrawal - navigate away
  useEffect(() => {
    if (orchestrator.store.state === "withdrawn") {
      // Small delay to show success state
      const timer = setTimeout(() => {
        navigate("/lock")
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [orchestrator.store.state, navigate])

  const complexTab: ComplexTab | undefined = MANAGE_LOCK_TABS[5]

  if (!complexTab) {
    return null
  }

  const title = `Withdraw`

  return (
    <PageContainer>
      <MetaData title={title} />
      <PageTitle title={title} items={breadcrumbs} />
      <TooltipProvider>
        <LockPageLayout>
          <LockPageTabs />
          <WithdrawCard
            store={orchestrator.store}
            governanceToken={governanceToken}
            icon={complexTab.icon ? <complexTab.icon /> : undefined}
            onCancel={() => {
              orchestrator.cancel()
            }}
            onReset={() => {
              orchestrator.executeReset()
            }}
            onRetryReset={() => {
              orchestrator.retryReset()
            }}
            onRetryWithdraw={() => {
              orchestrator.retryWithdraw()
            }}
            onWithdraw={() => {
              orchestrator.executeWithdraw()
            }}
          />
        </LockPageLayout>
      </TooltipProvider>
    </PageContainer>
  )
})
